/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { CharacterLimitPlugin } from '@lexical/react/LexicalCharacterLimitPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HashtagPlugin } from '@lexical/react/LexicalHashtagPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { CAN_USE_DOM } from '@/components/LexicalEditor/shared/canUseDOM';
import { useSettings } from '../context/SettingsContext';
import { useSharedHistoryContext } from '../context/SharedHistoryContext';
import AutocompletePlugin from '../plugins/AutocompletePlugin';
import AutoLinkPlugin from '../plugins/AutoLinkPlugin';
import CodeActionMenuPlugin from '../plugins/CodeActionMenuPlugin';
import CodeHighlightPlugin from '../plugins/CodeHighlightPlugin';
import ContextMenuPlugin from '../plugins/ContextMenuPlugin';
import DragDropPaste from '../plugins/DragDropPastePlugin';
import DeleteResetPlugin from '../plugins/DeleteResetPlugin';
import DraggableBlockPlugin from '../plugins/DraggableBlockPlugin';
import FloatingLinkEditorPlugin from '../plugins/FloatingLinkEditorPlugin';
import ImagesPlugin from '../plugins/ImagesPlugin';
import LinkPlugin from '../plugins/LinkPlugin';
import ListMaxIndentLevelPlugin from '../plugins/ListMaxIndentLevelPlugin';
import { MaxLengthPlugin } from '../plugins/MaxLengthPlugin';
import TabFocusPlugin from '../plugins/TabFocusPlugin';
import TableCellActionMenuPlugin from '../plugins/TableActionMenuPlugin';
import TableCellResizer from '../plugins/TableCellResizer';
import TableOfContentsPlugin from '../plugins/TableOfContentsPlugin';
import ToolbarPlugin, { ReorganizeButtonProps } from '../plugins/ToolbarPlugin';
import YouTubePlugin from '../plugins/YouTubePlugin';
import ContentEditable from '../ui/ContentEditable';
import Placeholder from '../ui/Placeholder';
import AutoEmbedPlugin from '../plugins/AutoEmbedPlugin';

import {
  $getRoot,
  EditorState,
  LexicalEditor as EditorComponent,
} from 'lexical';

import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import EmbedCodePlugin from '@/components/LexicalEditor/plugins/EmbedCodePlugin';
import LexicalContext, {
  LexicalContextProps,
} from '@/components/LexicalEditor/LexicalContext';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $generateNodesFromDOM } from '@lexical/html';
import { PLAYGROUND_TRANSFORMERS } from '@/components/LexicalEditor/plugins/MarkdownTransformers';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import CollapsiblePlugin from '@/components/LexicalEditor/plugins/CollapsiblePlugin';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { LiveblocksPlugin } from '@liveblocks/react-lexical';
import NotificationsPopover from '@/components/LexicalEditor/liveblocks/NotificationsPopover';
import VersionsDialog from '@/components/LexicalEditor/liveblocks/VersionHistoryDialog';
import usePasteWithTableStyles from '@/components/LexicalEditor/BoardEditor/usePasteWithTableStyles';
import useDropImageAtPoint from '@/components/LexicalEditor/BoardEditor/useDropImageAtPoint';
import { $wrapTopLevelInlines } from '@/components/LexicalEditor/utils/wrapTopLevelInlines';
import {
  $selectDocumentEnd,
  isClickBelowContent,
} from '@/components/LexicalEditor/BoardEditor/selectDocumentEnd';

type EditorProps = {
  isSimple?: boolean;
  isLiveBlock?: boolean;
  placeholder?: string;
  /** 회의록 전용 '재정리 요청' 툴바 버튼 — 전달한 화면에서만 노출 (PORTAL-12850) */
  reorganizeButtonProps?: ReorganizeButtonProps;
  /** 화면 전용 플러그인 — composer 안에서 렌더돼 useLexicalComposerContext 사용 가능 */
  plugins?: React.ReactNode;
  onChange: (
    editorState: EditorState,
    editor: EditorComponent,
    tags: Set<string>,
  ) => void;
};

export type EditorRef = {
  clear: () => void;
  update: (content: string) => void;
  isFocused: () => boolean;
};

const Editor = forwardRef<EditorRef, EditorProps>((props, ref) => {
  const {
    onChange,
    placeholder,
    isSimple,
    isLiveBlock,
    reorganizeButtonProps,
    plugins,
  } = props;
  const { t } = useTranslation();
  const { historyState } = useSharedHistoryContext();
  const {
    settings: {
      isAutocomplete,
      isMaxLength,
      isCharLimit,
      isCharLimitUtf8,
      isRichText,
      showTreeView,
      showTableOfContents,
      shouldUseLexicalContextMenu,
      tableCellMerge,
      tableCellBackgroundColor,
    },
  } = useSettings();
  const isEditable = useLexicalEditable();
  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);
  const [floatingAnchorElem, setFloatingAnchorElem] =
    useState<HTMLDivElement | null>(null);
  const [isSmallWidthViewport, setIsSmallWidthViewport] =
    useState<boolean>(false);
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState(false);
  const placeholderComponent = (
    <Placeholder>{placeholder ?? t('내용을 입력해 주세요.')}</Placeholder>
  );
  const editorRef = useRef<HTMLDivElement | null>(null);
  const isUpdatingRef = useRef(false);

  usePasteWithTableStyles(editor);
  useDropImageAtPoint(editor);

  useImperativeHandle(ref, () => ({
    clear: () => {
      editor.update(() => {
        const root = $getRoot();
        root.clear();
      });
    },
    update: (content: string) => {
      // 이미 업데이트 중이면 무시
      if (isUpdatingRef.current) {
        return;
      }

      isUpdatingRef.current = true;

      // 업데이트 전에 포커스 방지
      const rootElement = editor.getRootElement();
      if (rootElement) {
        rootElement.setAttribute('contenteditable', 'false');
      }

      editor.update(() => {
        const root = $getRoot();
        root.clear();

        const doc = new DOMParser().parseFromString(content, 'text/html');
        const nodes = $generateNodesFromDOM(editor, doc);

        // root 직접 append 경로 — 최상위 인라인은 #282 회피용 문단 감싸기 필수
        root.append(...$wrapTopLevelInlines(nodes));
      });

      // update 직후 contenteditable 복원 및 포커스 제거
      requestAnimationFrame(() => {
        editor.blur();

        if (rootElement) {
          rootElement.setAttribute('contenteditable', 'true');
          rootElement.blur();
        }

        // 업데이트 완료 후 플래그 해제
        isUpdatingRef.current = false;
      });
    },
    isFocused: () => isFocused, // 포커스 상태를 외부에서 확인할 수 있도록 제공
  }));

  const toolbarClick = () => {
    setIsFocused(true);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  // 콘텐츠 밖(스크롤러 여백·본문 아래 빈 영역) 클릭 시에도 에디터 포커스 + 커서를 끝으로 → 아무데나 클릭해 바로 입력.
  const handleEditorAreaClick = (e: React.MouseEvent) => {
    if (!isClickBelowContent(editor, e.target as HTMLElement, e.clientY))
      return;
    // 드래그 선택이 여백에서 끝난 경우엔 선택을 무너뜨리지 않음
    if (window.getSelection()?.isCollapsed === false) return;
    setIsFocused(true);
    editor.update(() => {
      $selectDocumentEnd();
    });
    editor.focus();
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!editorRef.current?.contains(document.activeElement)) {
        setIsFocused(false);
      }
    }, 0);
  };

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  useEffect(() => {
    const updateViewPortWidth = () => {
      const isNextSmallWidthViewport =
        CAN_USE_DOM && window.matchMedia('(max-width: 1025px)').matches;

      if (isNextSmallWidthViewport !== isSmallWidthViewport) {
        setIsSmallWidthViewport(isNextSmallWidthViewport);
      }
    };
    updateViewPortWidth();
    window.addEventListener('resize', updateViewPortWidth);

    return () => {
      window.removeEventListener('resize', updateViewPortWidth);
    };
  }, [isSmallWidthViewport]);

  useEffect(() => {
    return () => {
      setIsFocused(false);
    };
  }, []);

  return (
    <div
      ref={editorRef}
      className={`editor-shell-inner ${isFocused ? 'editing' : ''}`}
    >
      {isLiveBlock && (
        <>
          <VersionsDialog />
          <NotificationsPopover />
        </>
      )}

      {isRichText && (
        <ToolbarPlugin
          editor={editor}
          activeEditor={activeEditor}
          setActiveEditor={setActiveEditor}
          setIsLinkEditMode={setIsLinkEditMode}
          isSimple={isSimple}
          toolbarClick={toolbarClick}
          reorganizeButtonProps={reorganizeButtonProps}
        />
      )}
      <div
        className={`editor-container ${showTreeView ? 'tree-view' : ''} ${
          !isRichText ? 'plain-text' : ''
        }`}
      >
        {isMaxLength && <MaxLengthPlugin maxLength={30} />}
        <DragDropPaste />
        <ClearEditorPlugin />
        <AutoEmbedPlugin />
        <HashtagPlugin />
        <AutoLinkPlugin />

        {isRichText ? (
          <>
            <HistoryPlugin externalHistoryState={historyState} />
            <RichTextPlugin
              contentEditable={
                <div
                  className="editor-scroller"
                  onClick={handleEditorAreaClick}
                >
                  <div className="editor" ref={onRef}>
                    <ContentEditable
                      onClick={handleFocus}
                      onBlur={handleBlur}
                      autoFocus={false} // 자동 포커스를 방지
                    />
                    {isLiveBlock && <LiveblocksPlugin />}
                  </div>
                </div>
              }
              placeholder={placeholderComponent}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <CodeHighlightPlugin />
            <MarkdownShortcutPlugin transformers={PLAYGROUND_TRANSFORMERS} />
            <ListPlugin />
            <DeleteResetPlugin />
            <CheckListPlugin />
            <ListMaxIndentLevelPlugin maxDepth={7} />
            <TablePlugin
              hasCellMerge={tableCellMerge}
              hasCellBackgroundColor={tableCellBackgroundColor}
            />
            <TableCellResizer />
            <ImagesPlugin />
            <LinkPlugin />
            <YouTubePlugin />
            <EmbedCodePlugin />
            <CollapsiblePlugin />
            {!isEditable && <ClickableLinkPlugin disabled={isEditable} />}
            <TabFocusPlugin />
            <TabIndentationPlugin />
            <OnChangePlugin onChange={onChange} />
            {floatingAnchorElem && !isSmallWidthViewport && (
              <>
                <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
                <CodeActionMenuPlugin anchorElem={floatingAnchorElem} />
                <FloatingLinkEditorPlugin
                  anchorElem={floatingAnchorElem}
                  isLinkEditMode={isLinkEditMode}
                  setIsLinkEditMode={setIsLinkEditMode}
                />
                <TableCellActionMenuPlugin
                  anchorElem={floatingAnchorElem}
                  cellMerge={true}
                />
              </>
            )}
          </>
        ) : (
          <>
            <PlainTextPlugin
              contentEditable={<ContentEditable />}
              placeholder={placeholderComponent}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <HistoryPlugin externalHistoryState={historyState} />
          </>
        )}
        {(isCharLimit || isCharLimitUtf8) && (
          <CharacterLimitPlugin
            charset={isCharLimit ? 'UTF-16' : 'UTF-8'}
            maxLength={5}
          />
        )}
        {isAutocomplete && <AutocompletePlugin />}
        <div>{showTableOfContents && <TableOfContentsPlugin />}</div>
        {shouldUseLexicalContextMenu && <ContextMenuPlugin />}
        {plugins}
      </div>
    </div>
  );
});
Editor.displayName = 'BoardEditor';

const LexicalBoardEditor = forwardRef<
  EditorRef,
  EditorProps & LexicalContextProps
>((props, ref) => {
  const {
    onChange,
    isSimple,
    isLiveBlock,
    placeholder,
    reorganizeButtonProps,
    plugins,
    ...other
  } = props;

  return (
    <LexicalContext {...other}>
      <Editor
        ref={ref}
        isSimple={isSimple}
        isLiveBlock={isLiveBlock}
        onChange={onChange}
        placeholder={placeholder}
        reorganizeButtonProps={reorganizeButtonProps}
        plugins={plugins}
      />
    </LexicalContext>
  );
});
LexicalBoardEditor.displayName = 'LexicalBoardEditor';

export default LexicalBoardEditor;
